import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowSquareOut,
  CheckCircle,
  Warning,
  Phone,
  Lightning,
  CurrencyDollar,
  Clock,
} from "@phosphor-icons/react";

// Step content lives inline — this is a guide, not a feature. It's
// optimized to be read once during Twilio setup, not maintained.

const STEPS = [
  {
    n: 1,
    title: "Buy a 10-digit US phone number in Twilio Console",
    minutes: 5,
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          Open Twilio Console → <b>Phone Numbers → Manage → Buy a Number</b>.
          Filter:
        </p>
        <ul className="mt-2 space-y-1 text-sm text-[#4A5C50] list-disc list-inside">
          <li>Country: <b>United States</b></li>
          <li>Capabilities: check <b>Voice</b>, <b>SMS</b>, AND <b>MMS</b></li>
          <li>Type: <b>Local</b> (NOT Toll-Free — that's what blocked you the first time)</li>
        </ul>
        <p className="text-[#4A5C50] mt-3">
          Pick any area code (415 / 213 / 718 / 312 — doesn't matter what city
          the number "comes from"; carriers don't care). Click <b>Buy</b>.
          Cost: <b>~$1.15/month</b>.
        </p>
        <a
          href="https://console.twilio.com/us1/develop/phone-numbers/manage/search"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Open Twilio → Buy a Number <ArrowSquareOut size={14} />
        </a>
      </>
    ),
  },
  {
    n: 2,
    title: "Register your Brand (one-time, ~5 min)",
    minutes: 5,
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          Go to <b>Messaging → Regulatory Compliance → Brand Registration</b>.
          Click <b>+ Create a new Brand</b>. Fill in:
        </p>
        <ul className="mt-2 space-y-1 text-sm text-[#4A5C50] list-disc list-inside">
          <li>Legal business name (must match IRS records)</li>
          <li>EIN (Tax ID) — for sole proprietorships, use your SSN</li>
          <li>Business type, registration country (US), website (<span className="mono">https://www.zcardon.com</span>)</li>
          <li>Stock ticker if public (skip for Zcardon)</li>
        </ul>
        <p className="text-[#4A5C50] mt-3">
          <b>One-time cost: ~$4.</b> Vetting takes <b>1–3 hours</b> (sometimes
          minutes). You'll get an email when it's approved.
        </p>
        <a
          href="https://console.twilio.com/us1/develop/sms/regulatory-compliance/brands"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Open Twilio → Brand Registration <ArrowSquareOut size={14} />
        </a>
      </>
    ),
  },
  {
    n: 3,
    title: "Register your Campaign (one-time, ~5 min)",
    minutes: 5,
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          After the Brand is approved, go to <b>Messaging → Regulatory
          Compliance → Campaigns</b> → <b>+ Create a new Campaign</b>.
          This describes what kind of SMS you'll send so carriers know
          you're legit. Use these values for Zcardon:
        </p>
        <div className="mt-3 rounded-xl border border-[#E5E0D8] bg-white p-3 text-sm">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Use case</p>
          <p className="mono">Low-Volume Mixed</p>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mt-2">Description</p>
          <p className="text-[#112318] leading-relaxed">
            Customer-initiated virtual debit card delivery. Senders use
            Zcardon to issue a single-use prepaid card to a chosen receiver;
            after the sender completes payment, Zcardon texts the receiver
            an SMS containing the card number, expiry, CVV, and a short link
            to the rendered card image. Receivers are explicitly entered by
            the sender at checkout and have consented (per Zcardon's Terms
            of Service) to receive the card delivery message.
          </p>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mt-2">Sample message</p>
          <p className="mono text-xs leading-relaxed">
            Hi Alex! Here's your Zcardon card 💳<br/>
            Card: 4989-XXXX-XXXX-4208 · Exp 05/29 · CVV 288<br/>
            Balance: $5.00<br/>
            View card: https://www.zcardon.com/cards/9f5d4073<br/>
            Reply STOP to opt out.
          </p>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mt-2">Opt-in workflow</p>
          <p className="text-[#112318] leading-relaxed">
            The sender (Zcardon customer) explicitly enters the receiver's
            phone number on the card-issuance form. Zcardon's checkout flow
            includes a Terms of Service checkbox confirming the sender has
            permission to text the receiver. Receivers can reply STOP at
            any time to opt out (Twilio Advanced Opt-Out is enabled on the
            account).
          </p>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mt-2">Number of phone numbers</p>
          <p className="mono">1</p>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mt-2">Average msgs / number / day</p>
          <p className="mono">10–50</p>
        </div>
        <p className="text-[#4A5C50] mt-3">
          <b>One-time cost: ~$10. Plus ~$2/month per campaign.</b> Carrier
          vetting takes <b>4–24 hours</b>. Once approved, attach your new
          10DLC number to this campaign in the same screen.
        </p>
        <a
          href="https://console.twilio.com/us1/develop/sms/regulatory-compliance/campaigns"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Open Twilio → Campaigns <ArrowSquareOut size={14} />
        </a>
      </>
    ),
  },
  {
    n: 4,
    title: "Update Zcardon's TWILIO_PHONE_NUMBER env var",
    minutes: 2,
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          Once your campaign is approved, copy your new 10DLC number
          (format: <span className="mono">+14155551234</span>). Open the
          Emergent Deploy Panel and update:
        </p>
        <div className="mt-3 rounded-xl border border-[#E5E0D8] bg-white p-3 text-sm">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Variable to update</p>
          <p className="mono text-[#112318] mt-1">TWILIO_PHONE_NUMBER</p>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mt-3">Old value (toll-free, blocked)</p>
          <p className="mono text-[#B04E2F] mt-1 line-through">+18669019945</p>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mt-3">New value (10DLC, works)</p>
          <p className="mono text-[#184A30] mt-1">+1XXXXXXXXXX</p>
        </div>
        <p className="text-[#4A5C50] mt-3">
          Redeploy production. After a redeploy, click <b>"Save to GitHub"</b>
          → <b>Deploy Panel</b> → <b>Redeploy</b>. Then issue a test card to
          your own phone to verify backend SMS arrives.
        </p>
      </>
    ),
  },
  {
    n: 5,
    title: "Cancel the toll-free TFV (optional but recommended)",
    minutes: 2,
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          Once 10DLC is working, you no longer need the toll-free number
          <span className="mono"> +18669019945</span> or its pending TFV
          submission. To stop the ~$2/month toll-free fee:
        </p>
        <ul className="mt-2 space-y-1 text-sm text-[#4A5C50] list-disc list-inside">
          <li>Twilio Console → Phone Numbers → Manage → Active Numbers</li>
          <li>Click <span className="mono">+18669019945</span> → <b>Release this number</b></li>
        </ul>
        <p className="text-[#4A5C50] mt-3">
          Or keep it as a backup — your call. The 10DLC number will be the
          one Zcardon uses for all outbound SMS once you update the env var.
        </p>
      </>
    ),
  },
];

const COST_ROWS = [
  { label: "10DLC phone number", once: null, monthly: "$1.15", note: "rental fee" },
  { label: "Brand Registration", once: "$4", monthly: null, note: "one-time vetting fee" },
  { label: "Campaign Registration", once: "$10", monthly: "$2", note: "one-time + monthly campaign fee" },
  { label: "SMS cost per message (US)", once: null, monthly: "$0.0083", note: "outbound, per segment" },
  { label: "MMS cost per message (US)", once: null, monthly: "$0.02", note: "outbound, per message with image" },
];

export default function Twilio10dlcGuide() {
  const totalMonthly = 1.15 + 2.0;
  const totalOneTime = 4 + 10;
  return (
    <div className="min-h-screen bg-[#FBF9F6] text-[#112318]" data-testid="twilio-10dlc-guide">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link
          to="/admin/setup"
          className="inline-flex items-center gap-1 text-sm text-[#4A5C50] hover:text-[#184A30]"
          data-testid="twilio-10dlc-back"
        >
          <ArrowLeft size={14} /> Back to Setup Checklist
        </Link>

        <header className="mt-6">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">
            US carrier compliance · the real fix
          </p>
          <h1 className="serif text-4xl mt-1">
            Switch to Twilio 10DLC
          </h1>
          <p className="text-[#4A5C50] mt-3 leading-relaxed max-w-2xl">
            Your current Twilio number <span className="mono">+18669019945</span> is
            <b> toll-free</b>, which US carriers (AT&amp;T, Verizon, T-Mobile)
            block until you pass <b>Toll-Free Verification</b> (currently
            stuck IN_REVIEW). The reliable alternative: buy a regular 10-digit
            local US number ("10DLC") which uses a different — and much
            faster — approval pipeline. Total setup ~15 minutes of clicking +
            4–24 hours of carrier review. Total cost: <b>${totalOneTime} one-time + ${totalMonthly.toFixed(2)}/month</b>.
          </p>
        </header>

        {/* TL;DR */}
        <section
          className="mt-6 rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-5"
          data-testid="twilio-10dlc-tldr"
        >
          <div className="flex items-start gap-3">
            <Lightning size={22} weight="fill" className="text-[#8C6B21] shrink-0 mt-1" />
            <div>
              <p className="serif text-lg text-[#8C6B21]">TL;DR</p>
              <p className="text-sm text-[#8C6B21] mt-1 leading-relaxed">
                Buy a 10DLC number → register Brand (5 min) → register
                Campaign (5 min) → update <span className="mono">TWILIO_PHONE_NUMBER</span> env var → redeploy.
                Approval in hours, not weeks. Backend SMS to US receivers
                works the moment the campaign is approved.
              </p>
            </div>
          </div>
        </section>

        {/* Cost summary */}
        <section className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5">
          <div className="flex items-center gap-2">
            <CurrencyDollar size={18} weight="duotone" className="text-[#184A30]" />
            <h3 className="serif text-lg">Cost breakdown</h3>
          </div>
          <div className="mt-4 divide-y divide-[#E5E0D8]">
            {COST_ROWS.map((r) => (
              <div key={r.label} className="py-2 flex items-baseline justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{r.label}</p>
                  <p className="text-xs text-[#4A5C50]">{r.note}</p>
                </div>
                <div className="flex items-baseline gap-3 shrink-0">
                  {r.once && <span className="mono text-sm"><span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">once </span>{r.once}</span>}
                  {r.monthly && <span className="mono text-sm"><span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">monthly </span>{r.monthly}</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-[#E5E0D8] flex items-baseline justify-between gap-3">
            <p className="serif text-base">Total commitment</p>
            <p className="mono text-sm">
              ${totalOneTime} one-time + ${totalMonthly.toFixed(2)}/mo
            </p>
          </div>
        </section>

        {/* Steps */}
        <ol className="mt-8 space-y-5" data-testid="twilio-10dlc-steps">
          {STEPS.map((s) => (
            <li
              key={s.n}
              className="rounded-2xl border border-[#E5E0D8] bg-white p-5"
              data-testid={`twilio-10dlc-step-${s.n}`}
            >
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-9 h-9 rounded-full bg-[#184A30] text-white flex items-center justify-center text-sm serif">
                  {s.n}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="serif text-xl">{s.title}</h2>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50] inline-flex items-center gap-1">
                      <Clock size={11} /> ~{s.minutes} min
                    </span>
                  </div>
                  <div className="mt-2 text-sm">{s.body}</div>
                </div>
              </div>
            </li>
          ))}
        </ol>

        {/* FAQ */}
        <section className="mt-8 rounded-2xl border border-[#E5E0D8] bg-white p-5">
          <h3 className="serif text-lg">FAQ</h3>
          <div className="mt-3 space-y-3 text-sm text-[#112318]">
            <div>
              <p className="font-semibold">Will my pending Toll-Free Verification still complete?</p>
              <p className="text-[#4A5C50] mt-0.5">
                Yes — TFV review continues independently. If it ever
                approves, you'll have BOTH toll-free + 10DLC available.
                But 10DLC is faster and more reliable, so we route everything
                through it once configured.
              </p>
            </div>
            <div>
              <p className="font-semibold">Can I send MMS (image attachments) on 10DLC?</p>
              <p className="text-[#4A5C50] mt-0.5">
                Yes — make sure to enable MMS capability when buying the
                number (step 1). US carrier MMS works on 10DLC the same way
                it would on toll-free (once approved). International
                receivers still get SMS-only (carriers outside US/CA don't
                support cross-border MMS — this is unrelated to 10DLC).
              </p>
            </div>
            <div>
              <p className="font-semibold">What if my Brand Registration gets rejected?</p>
              <p className="text-[#4A5C50] mt-0.5">
                Rare for legitimate businesses. Most common reason: the
                legal business name doesn't match IRS records exactly. Edit
                and resubmit — no extra fee for resubmission of a rejected
                brand.
              </p>
            </div>
            <div>
              <p className="font-semibold">Do I need anything else for the receiver-side experience?</p>
              <p className="text-[#4A5C50] mt-0.5">
                No. Once <span className="mono">TWILIO_PHONE_NUMBER</span> is
                updated and the campaign is approved, the existing Zcardon
                code automatically routes backend SMS through the new number.
                The receiver sees a regular 10-digit US number, not a
                toll-free 866. Deliverability improves significantly.
              </p>
            </div>
          </div>
        </section>

        {/* Status indicator at bottom */}
        <section
          className="mt-8 rounded-2xl border border-[#D96C4A]/40 bg-[#D96C4A]/5 p-5"
          data-testid="twilio-10dlc-current-state"
        >
          <div className="flex items-start gap-3">
            <Warning size={20} weight="fill" className="text-[#B04E2F] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="serif text-base text-[#B04E2F]">Current state</p>
              <p className="text-sm text-[#B04E2F] mt-1 leading-relaxed">
                Backend SMS to US receivers is <b>blocked</b> at the carrier
                level (Twilio error <span className="mono">30032</span>).
                Zcardon falls back to native iMessage (when sender is on
                iPhone) or auto-email (when sender is on desktop) — both
                work today, but neither is automatic backend SMS+MMS.
                Following this guide is the only way to get true
                "click → receiver's phone buzzes with card image" delivery.
              </p>
            </div>
          </div>
        </section>

        <div className="mt-8 text-center">
          <Link
            to="/admin/setup"
            className="text-sm text-[#184A30] hover:underline"
          >
            ← Back to Setup Checklist
          </Link>
        </div>
      </div>
    </div>
  );
}
